import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

// ============================================
// Case Study Data
// ============================================
interface CaseStudy {
  id: string;
  headline: string;
  shortTitle: string;
  companyType: string;
  industry: string;
  service: string;
  technologies: string[];
  situation: string;
  challenge: string;
  solution: string;
  metrics: Array<{ value: string; label: string }>;
  roi: string;
  timeline: string;
  thumbnail?: string;
  videoUrl?: string;
  quote: {
    text: string;
    author: string;
    company: string;
  };
  featured?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const caseStudies: CaseStudy[] = [
  // ============================================
  // AI IMPLEMENTATION CASE STUDIES
  // ============================================
  {
    id: 'real-estate-marketing-intelligence',
    headline: 'How a Real Estate Company Automated Marketing Analytics Across 7 Platforms — Saving 3 Hours Daily',
    shortTitle: 'Real Estate Marketing Intelligence System',
    companyType: 'Real Estate Developer',
    industry: 'Real Estate',
    service: 'AI Implementation',
    technologies: ['Make.com', 'Airtable', 'React', 'Google Analytics API', 'Meta API', 'YouTube Analytics API'],
    situation: `This vertically integrated Bali real estate company was managing marketing data across Google Analytics, Search Console, Instagram, Facebook, LinkedIn, YouTube, and Mailchimp. Their marketing team spent 15+ hours weekly manually pulling data from each platform, copying numbers into spreadsheets, and creating reports. Data was often outdated by the time decisions were made, and cross-platform insights were nearly impossible.`,
    challenge: `Manual data collection from 7 platforms was taking 3+ hours every Monday. By the time reports were compiled, the data was already stale. Cross-platform attribution was impossible.`,
    solution: `We built an automated marketing intelligence system using Make.com scenarios that scrape daily metrics from all 7 platforms and push them into a centralized Airtable database. On top of that, we created a custom React dashboard that pulls from Airtable via API, featuring cross-platform comparison tools, trend analysis, and one-click PDF report generation for stakeholders.`,
    metrics: [
      { value: '7 → 1', label: 'Data sources unified' },
      { value: '$96K', label: 'Annual labor savings' },
      { value: '3 hrs/day', label: 'Time saved daily' },
      { value: '100%', label: 'Data accuracy' },
    ],
    roi: '380%',
    timeline: '6 weeks',
    quote: {
      text: "We used to spend 3 hours every Monday pulling numbers from 7 different platforms. Now the dashboard updates itself daily and the team focuses on strategy instead of spreadsheets. The cross-platform comparison feature alone changed how we allocate our ad budget.",
      author: 'Head of Marketing',
      company: 'Bali Real Estate Developer',
    },
    featured: true,
    size: 'large',
  },
  {
    id: 'ai-property-render-pipeline',
    headline: 'How a Property Developer Automated Luxury Render Variations — Cutting Costs by 85%',
    shortTitle: 'AI-Enhanced Property Render Pipeline',
    companyType: 'Property Development',
    industry: 'Real Estate',
    service: 'AI Implementation',
    technologies: ['n8n', 'Freepik Reimagine API', 'Google Drive', 'Gemini AI', 'Make.com'],
    situation: `This property developer was paying external 3D artists $150–200 per styled render to create marketing materials for their villa projects. Each project needed multiple style variations (Moroccan, Italian, Ultra-Luxury Modern) for different buyer demographics. The process took days, cost thousands per project, and created bottlenecks in their marketing timeline.`,
    challenge: `$150-200 per render, days of turnaround, and constant bottlenecks in the marketing pipeline. Scaling meant scaling costs linearly.`,
    solution: `We built an automated render enhancement pipeline using n8n and the Freepik Reimagine API. The system watches a Google Drive folder for new base renders, automatically processes each image through three style-specific AI prompts, and uploads the finished variations back to organized Drive folders.`,
    metrics: [
      { value: '45 min → 3 min', label: 'Per render time' },
      { value: '$28K', label: 'Annual savings' },
      { value: '3 styles', label: 'Auto-generated per photo' },
      { value: '85%', label: 'Cost reduction' },
    ],
    roi: '520%',
    timeline: '3 weeks',
    quote: {
      text: "We went from paying $200 per styled render to generating three luxury variations automatically. The Moroccan and Italian styles actually sell better than our original renders. We've completely eliminated our external render budget for marketing materials.",
      author: 'Creative Director',
      company: 'Property Development Firm',
    },
    size: 'medium',
  },
  {
    id: 'automated-contract-generation',
    headline: 'How a Real Estate Company Cut Contract Turnaround From 2 Days to 15 Minutes',
    shortTitle: 'Automated Contract Generation System',
    companyType: 'Real Estate Investment',
    industry: 'Real Estate',
    service: 'AI Implementation',
    technologies: ['n8n', 'PandaDoc API', 'Airtable', 'Slack', 'Webhook'],
    situation: `This real estate investment company was handling contract generation manually. Sales would close a deal, then email legal with buyer details, unit information, payment schedules, and pricing. Legal would spend 1–2 days manually populating Word templates, often introducing errors from copy-pasting across multiple data sources.`,
    challenge: `2-day contract turnaround was losing deals. Buyers who waited too long would go cold or find competing properties. Manual data entry caused frequent errors.`,
    solution: `We restructured their Airtable SSOT with normalized tables for Clients, Companies, Deals, Units, and Projects. Then we built an n8n workflow triggered by a status change: when a deal moves to "Contract Ready," the system automatically populates a PandaDoc template and sends it for e-signature.`,
    metrics: [
      { value: '2 days → 15 min', label: 'Contract turnaround' },
      { value: '$54K', label: 'Annual savings' },
      { value: '100%', label: 'Template accuracy' },
      { value: 'Zero', label: 'Manual data entry' },
    ],
    roi: '340%',
    timeline: '4 weeks',
    quote: {
      text: "Our sales team used to wait 2 days for legal to prepare each contract. Now they click a button in Airtable and a fully populated PPJB is in the buyer's inbox in 15 minutes. We've closed 3 deals we would have lost to the old timeline.",
      author: 'VP of Sales',
      company: 'Real Estate Investment Company',
    },
    size: 'medium',
  },
  {
    id: 'voice-ai-appointment-setter',
    headline: 'How a UAE Wellness Clinic Doubled Consultation Bookings With Voice AI — Without Hiring',
    shortTitle: 'Voice AI Appointment Setter',
    companyType: 'Healthcare/Wellness',
    industry: 'Healthcare',
    service: 'AI Implementation',
    technologies: ['Vapi', 'Make.com', 'Twilio', 'Airtable', 'Google Calendar', 'ElevenLabs'],
    situation: `This premium wellness clinic in Dubai was spending 50,000+ AED/month on Meta ads generating 180 leads monthly. But their reception team took 2–3 hours to call leads back, and follow-up was inconsistent. Only 25 consultations were booked monthly from 180 leads — a 14% conversion rate.`,
    challenge: `Leads were going cold while waiting for callbacks. The clinic was hemorrhaging money on ads that generated leads they couldn't convert fast enough.`,
    solution: `We implemented an AI-powered voice appointment setter using Vapi for voice AI, Twilio for UAE phone numbers, and Make.com for orchestration. The system triggers within 15 minutes of a form submission, qualifies leads, and books consultations directly into Google Calendar.`,
    metrics: [
      { value: '3 hrs → 15 min', label: 'Lead response time' },
      { value: '108%', label: 'More consultations booked' },
      { value: '82%', label: 'Show-up rate' },
      { value: '24/7', label: 'Availability' },
    ],
    roi: '620%',
    timeline: '5 weeks',
    quote: {
      text: "The AI books more consultations than our receptionist did, and my team finally has time to focus on closing. Our show-up rate went from 55% to 82%. The system paid for itself in the first week.",
      author: 'Owner',
      company: 'Premium Wellness Clinic (UAE)',
    },
    featured: true,
    size: 'large',
  },
  {
    id: 'automated-seo-blog-system',
    headline: 'How a Real Estate Company Automated Blog Production — Publishing 12 SEO Posts Monthly on Autopilot',
    shortTitle: 'Automated SEO Blog Publishing System',
    companyType: 'Real Estate Developer',
    industry: 'Real Estate',
    service: 'AI Implementation',
    technologies: ['n8n', 'WordPress REST API', 'OpenAI', 'Google Sheets', 'Yoast SEO', 'Airtable'],
    situation: `This real estate company knew they needed content marketing for SEO but couldn't justify the cost or time. Hiring a content writer was $2,000+/month, and internal team members were spending 4+ hours per article. Their blog sat dormant for months.`,
    challenge: `No execution capacity for content marketing. Competitors were outranking them on every key search term while their blog collected dust.`,
    solution: `We built a fully automated n8n workflow that pulls keyword clusters from Google Sheets, generates comprehensive long-form blog posts using AI, adds internal links, generates SEO metadata, and publishes directly to WordPress — all automatically.`,
    metrics: [
      { value: '4 hrs → 0', label: 'Manual time per post' },
      { value: '12 posts/month', label: 'Published automatically' },
      { value: '100%', label: 'SEO-optimized' },
      { value: '2x', label: 'Organic traffic increase' },
    ],
    roi: '290%',
    timeline: '2 weeks',
    quote: {
      text: "We publish 3 SEO-optimized blog posts per week now without anyone touching it. Our organic traffic doubled in 4 months and we haven't written a single word manually. The meta titles and descriptions are better than what our freelancer used to write.",
      author: 'Marketing Manager',
      company: 'Real Estate Developer',
    },
    size: 'small',
  },
  {
    id: 'lead-qualification-chatbot',
    headline: 'How a Bali Real Estate Company 3x\'d Site Visit Bookings With Automated Lead Qualification',
    shortTitle: 'Lead Qualification & Nurture Chatbot',
    companyType: 'Real Estate',
    industry: 'Real Estate',
    service: 'AI Implementation',
    technologies: ['ManyChat', 'Make.com', 'Airtable', 'WhatsApp Business API', 'Slack'],
    situation: `This Bali-based property company was getting strong engagement on Instagram but couldn't keep up. Leads asking "price?" at 11pm went unanswered until morning. By then, the prospect had messaged three competitors.`,
    challenge: `Sales team was wasting time on unqualified browsers while serious investors weren't getting prioritized. Response times were killing conversions.`,
    solution: `We implemented a ManyChat-powered automation system connected to Instagram and WhatsApp. Comment keywords trigger instant DM conversations, the chatbot qualifies leads, and hot leads get instant Slack notifications with full context.`,
    metrics: [
      { value: '60%', label: 'Leads auto-qualified' },
      { value: '3x', label: 'Site visit increase' },
      { value: '< 30 sec', label: 'Average response time' },
      { value: '24/7', label: 'Availability' },
    ],
    roi: '450%',
    timeline: '4 weeks',
    quote: {
      text: "Our Instagram DMs used to go unanswered for hours. Now every comment gets a response in seconds, and qualified leads get automatically pushed to our sales team with full context. We booked more site visits last month than the previous quarter combined.",
      author: 'Sales Director',
      company: 'Bali Property Company',
    },
    size: 'small',
  },

  // ============================================
  // VIBECODE CASE STUDIES
  // ============================================
  {
    id: 'property-management-platform',
    headline: 'How a Real Estate Group Built a Full Property Management SaaS in 3 Weeks — Not 3 Months',
    shortTitle: 'Real Estate Portfolio Management Platform',
    companyType: 'Real Estate Investment Group',
    industry: 'Real Estate',
    service: 'VibeCode',
    technologies: ['React 18', 'TypeScript', 'Supabase', 'Tailwind CSS', 'Claude Code'],
    situation: `This real estate investment group with multiple projects across Bali needed a centralized platform where each property agency could manage their own listings, track leads, and report to investors. Traditional development quotes came in at $80K–150K with 4–6 month timelines.`,
    challenge: `The CEO needed something now, not in six months, as three new projects were launching simultaneously. Budget was a fraction of traditional quotes.`,
    solution: `We used our VibeCode rapid development approach to build PropertyBase — a multi-tenant real estate SaaS in 21 days. The platform features subdomain-based tenant isolation, Row Level Security, role-based access control, and property listings management.`,
    metrics: [
      { value: '21 days', label: 'Concept to production' },
      { value: '70%+', label: 'Under agency quote' },
      { value: '4 tenants', label: 'Onboarded in month 1' },
      { value: '200+ hrs', label: 'Eng time saved' },
    ],
    roi: '480%',
    timeline: '21 days',
    quote: {
      text: "I was skeptical when they said three weeks. We'd been quoted 4-6 months by traditional agencies. But they shipped a working multi-tenant SaaS that our property managers are actually using daily. The tenant isolation means each agency only sees their own data — exactly what we needed.",
      author: 'CEO',
      company: 'Real Estate Investment Group',
    },
    featured: true,
    size: 'large',
  },
  {
    id: 'investor-fund-dashboard',
    headline: 'How a Real Estate Fund Built an Investor Portal in 2 Weeks — Attracting 3 New LPs',
    shortTitle: 'Investor Fund Analytics Dashboard',
    companyType: 'Real Estate Fund',
    industry: 'Finance',
    service: 'VibeCode',
    technologies: ['React', 'Airtable API', 'Recharts', 'Tailwind CSS', 'jsPDF'],
    situation: `This Bali real estate fund was managing investor communications through monthly PDF reports compiled manually in Excel. Updates took the team a full week to compile each month. Sophisticated investors were asking for real-time access to their portfolio data.`,
    challenge: `Manual reporting was becoming a barrier to raising new capital. Investors wanted real-time data, not month-old PDFs.`,
    solution: `We built a custom investor analytics dashboard using our VibeCode approach — React frontend connected directly to Airtable API. The dashboard features real-time portfolio overview, project-by-project tracking, and professional PDF export.`,
    metrics: [
      { value: '14 days', label: 'Concept to production' },
      { value: 'Week saved', label: 'Per month of reporting' },
      { value: '3 new LPs', label: 'Attracted by portal' },
      { value: 'Real-time', label: 'Data visibility' },
    ],
    roi: '350%',
    timeline: '14 days',
    quote: {
      text: "Our investors used to get monthly PDF reports that were outdated by the time we sent them. Now they log in and see their portfolio performance in real time. Three new investors committed specifically because of the dashboard — it showed them we run a data-driven operation.",
      author: 'Managing Partner',
      company: 'Real Estate Fund',
    },
    size: 'medium',
  },
  {
    id: 'programmatic-lead-generation',
    headline: 'How a Digital Agency Built a Programmatic Sales Machine in 12 Days — Generating 200+ Qualified Leads Monthly',
    shortTitle: 'Programmatic Lead Generation Engine',
    companyType: 'Digital Agency',
    industry: 'Agency',
    service: 'VibeCode',
    technologies: ['n8n', 'Apify', 'Cloudflare Workers', 'Gemini 2.0 Flash', 'Google Sheets', 'Telegram Bot'],
    situation: `This digital agency was relying entirely on referrals and cold outreach for new clients. Their sales team spent hours researching prospects manually — generating maybe 20-30 qualified conversations per month.`,
    challenge: `No scalable system for lead generation. Manual research couldn't keep up with growth targets.`,
    solution: `We built a full programmatic lead generation engine. The system uses Apify to discover businesses, enriches each lead with website quality scoring via Gemini AI, generates personalized landing pages, and pushes qualified leads to the sales team automatically.`,
    metrics: [
      { value: '12 days', label: 'Full system built' },
      { value: '200+', label: 'Qualified leads/month' },
      { value: '< $0.50', label: 'Cost per lead' },
      { value: 'Fully automated', label: 'End-to-end' },
    ],
    roi: '560%',
    timeline: '12 days',
    quote: {
      text: "They built us a system that finds businesses, scores their websites with AI, generates personalized landing pages, and delivers ready-to-call leads — all without us lifting a finger. Our pipeline went from 20 conversations a month to over 200.",
      author: 'Founder',
      company: 'Digital Agency',
    },
    size: 'medium',
  },

  // ============================================
  // AI AUDIT CASE STUDIES
  // ============================================
  {
    id: 'construction-operations-audit',
    headline: 'How a Construction Company Found $180K in Hidden Operational Waste — And Fixed It in 60 Days',
    shortTitle: 'Operations Automation Audit',
    companyType: 'Construction Company',
    industry: 'Construction',
    service: 'AI Audit Partnership',
    technologies: ['Process Mapping', 'Airtable', 'Make.com', 'n8n', 'Slack'],
    situation: `This in-house construction company was growing fast — 29 employees across three entities — but their operations hadn't scaled. Material requests were handled through WhatsApp, project updates were scattered, and the team spent hours on tasks that should have been automated.`,
    challenge: `They knew they were losing money to inefficiency but didn't know where to start or how to prioritize fixes.`,
    solution: `We conducted a comprehensive 2-week AI and automation audit. Discovery interviews, process mapping, automation scoring, and a prioritized implementation roadmap with estimated costs and expected savings for each automation.`,
    metrics: [
      { value: '14 processes', label: 'Identified for automation' },
      { value: '$180K', label: 'Projected annual savings' },
      { value: '60 days', label: 'To first ROI' },
      { value: '3 automations', label: 'Implemented immediately' },
    ],
    roi: '310%',
    timeline: '2 weeks',
    quote: {
      text: "They mapped every process in our company, found where we were bleeding money, and gave us a prioritized roadmap. We implemented the top 3 recommendations and saved more in 2 months than the audit cost. Now we have a clear 12-month automation plan.",
      author: 'Operations Director',
      company: 'Construction Company',
    },
    size: 'medium',
  },
  {
    id: 'ecommerce-marketing-audit',
    headline: 'How an E-commerce Brand Discovered $42K in Wasted Ad Spend — And Consolidated 7 Tools to 3',
    shortTitle: 'Marketing Technology Audit',
    companyType: 'DTC E-commerce',
    industry: 'E-commerce',
    service: 'AI Audit Partnership',
    technologies: ['Analytics Audit', 'Make.com', 'Airtable', 'Meta API', 'Google Ads API'],
    situation: `This DTC fashion brand was running campaigns across Meta, Google, TikTok, and email with no unified view of performance. They had seven separate marketing tools with overlapping features and broken attribution.`,
    challenge: `Spending $15K/month on ads but couldn't prove ROI. Team members manually exported CSVs every week. Nobody could answer which channel drove the most profitable customers.`,
    solution: `We conducted a full marketing stack assessment, discovered three tools had near-identical functionality, and designed a consolidated architecture using Airtable as the central hub with automated data collection via Make.com.`,
    metrics: [
      { value: '$42K', label: 'Wasted spend identified' },
      { value: '7 → 3', label: 'Tools consolidated' },
      { value: '40%', label: 'Budget reallocation' },
      { value: '2 weeks', label: 'To first results' },
    ],
    roi: '280%',
    timeline: '10 days',
    quote: {
      text: "We were paying for 7 different marketing tools with overlapping features and had no idea which channels were actually driving sales. The audit saved us more than it cost before they even started implementing. We reallocated 40% of our ad budget based on their findings.",
      author: 'CMO',
      company: 'DTC Fashion Brand',
    },
    size: 'small',
  },
  {
    id: 'property-management-ai-assessment',
    headline: 'How a Property Management Company Avoided Hiring 3 People — By Finding 23 Tasks to Automate',
    shortTitle: 'AI Readiness Assessment',
    companyType: 'Property Management',
    industry: 'Property Management',
    service: 'AI Audit Partnership',
    technologies: ['AI Assessment', 'Process Audit', 'Airtable', 'n8n'],
    situation: `This licensed property management agency in Bali was managing a growing portfolio of vacation rental villas. With each new property, the administrative burden grew linearly. They were planning to hire three new staff members at $120K/year combined.`,
    challenge: `Workload was scaling linearly with portfolio growth. The choice seemed to be: hire more people or stop growing.`,
    solution: `We conducted an 8-day AI readiness assessment, mapping every operational process and identifying 23 distinct tasks that could be fully or partially automated — from guest messaging to owner reporting to financial reconciliation.`,
    metrics: [
      { value: '23 tasks', label: 'Automation candidates' },
      { value: '$120K', label: 'Projected savings' },
      { value: '60%', label: 'Work automatable' },
      { value: '8 days', label: 'Audit duration' },
    ],
    roi: '260%',
    timeline: '8 days',
    quote: {
      text: "We thought we needed to hire 3 more people to handle our growing portfolio. The audit showed us we could automate 60% of the work we were about to hire for. That's 3 salaries saved. The roadmap they delivered is now our operations bible for the next 12 months.",
      author: 'Director',
      company: 'Property Management Agency',
    },
    size: 'small',
  },
];

// Filter categories
const industries = ['All', 'Real Estate', 'Healthcare', 'Finance', 'E-commerce', 'Agency', 'Construction', 'Property Management'];
const services = ['All', 'AI Implementation', 'VibeCode', 'AI Audit Partnership'];

// ============================================
// Case Study Card Component (Enterprise)
// ============================================
interface CaseStudyCardProps {
  study: CaseStudy;
  onPreview: (study: CaseStudy) => void;
  index: number;
}

const CaseStudyCard: React.FC<CaseStudyCardProps> = ({ study, onPreview, index }) => {
  // Get service color
  const serviceColor = study.service === 'AI Implementation'
    ? 'blue'
    : study.service === 'VibeCode'
      ? 'violet'
      : 'emerald';

  const colorClasses = {
    blue: {
      badge: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
      accent: 'text-blue-400',
      icon: 'bg-blue-500/10 text-blue-400',
    },
    violet: {
      badge: 'bg-violet-500/10 border-violet-500/20 text-violet-400',
      accent: 'text-violet-400',
      icon: 'bg-violet-500/10 text-violet-400',
    },
    emerald: {
      badge: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
      accent: 'text-emerald-400',
      icon: 'bg-emerald-500/10 text-emerald-400',
    },
  };

  const colors = colorClasses[serviceColor];

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 30 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="group cursor-pointer"
      onClick={() => onPreview(study)}
    >
      <div className="h-full rounded-2xl bg-zinc-900/50 border border-zinc-800/50 hover:border-zinc-700/50 transition-all duration-300 hover:shadow-lg hover:shadow-black/20 overflow-hidden">
        {/* Header with badges */}
        <div className="p-6 pb-4">
          <div className="flex items-center justify-between mb-4">
            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${colors.badge}`}>
              {study.service}
            </span>
            <span className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-bold">
              {study.roi} ROI
            </span>
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold text-white mb-2 group-hover:text-emerald-400 transition-colors line-clamp-2">
            {study.shortTitle}
          </h3>

          {/* Industry & Timeline */}
          <div className="flex items-center gap-3 text-sm text-zinc-500">
            <span>{study.industry}</span>
            <span className="w-1 h-1 rounded-full bg-zinc-700" />
            <span>{study.timeline}</span>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="px-6 pb-4">
          <div className="grid grid-cols-2 gap-3">
            {study.metrics.slice(0, 2).map((metric, i) => (
              <div key={i} className="p-3 rounded-xl bg-zinc-800/30">
                <p className={`text-lg font-bold ${colors.accent}`}>{metric.value}</p>
                <p className="text-xs text-zinc-500 mt-0.5">{metric.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Quote preview */}
        <div className="px-6 pb-4">
          <p className="text-sm text-zinc-400 italic line-clamp-2">
            "{study.quote.text}"
          </p>
          <p className="text-xs text-zinc-500 mt-2">— {study.quote.author}, {study.quote.company}</p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-800/50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {study.technologies.slice(0, 2).map((tech) => (
              <span key={tech} className="px-2 py-1 rounded-md bg-zinc-800/50 text-zinc-500 text-xs">
                {tech}
              </span>
            ))}
            {study.technologies.length > 2 && (
              <span className="text-zinc-600 text-xs">+{study.technologies.length - 2}</span>
            )}
          </div>
          <div className="flex items-center gap-2 text-zinc-400 text-sm group-hover:text-white transition-colors">
            <span>View</span>
            <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ============================================
// Filter Pills Component (Enterprise)
// ============================================
interface FilterPillsProps {
  options: string[];
  selected: string;
  onChange: (value: string) => void;
}

const FilterPills: React.FC<FilterPillsProps> = ({ options, selected, onChange }) => {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => (
        <button
          key={option}
          onClick={() => onChange(option)}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
            selected === option
              ? 'bg-white text-zinc-900'
              : 'bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800 hover:text-white border border-zinc-700/50'
          }`}
        >
          {option}
        </button>
      ))}
    </div>
  );
};

// ============================================
// Search Input Component
// ============================================
interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
}

const SearchInput: React.FC<SearchInputProps> = ({ value, onChange }) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className="relative">
      <motion.div
        className="absolute inset-0 rounded-xl"
        animate={isFocused ? {
          boxShadow: '0 0 0 4px rgba(16, 185, 129, 0.1), 0 0 20px rgba(16, 185, 129, 0.1)'
        } : {
          boxShadow: '0 0 0 0px rgba(16, 185, 129, 0)'
        }}
      />
      <div className="relative flex items-center">
        <svg
          className={`absolute left-4 w-5 h-5 transition-colors ${isFocused ? 'text-emerald-400' : 'text-zinc-500'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          placeholder="Search case studies..."
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className="w-full pl-12 pr-4 py-3 rounded-xl bg-zinc-900/50 border border-zinc-800/50 text-white placeholder-zinc-500 focus:outline-none focus:border-emerald-500/30 transition-colors"
        />
        {value && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={() => onChange('')}
            className="absolute right-4 w-5 h-5 rounded-full bg-zinc-700 hover:bg-zinc-600 flex items-center justify-center transition-colors"
          >
            <svg className="w-3 h-3 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </motion.button>
        )}
      </div>
    </div>
  );
};

// ============================================
// Case Study Preview Modal
// ============================================
interface PreviewModalProps {
  study: CaseStudy | null;
  onClose: () => void;
}

const PreviewModal: React.FC<PreviewModalProps> = ({ study, onClose }) => {
  if (!study) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-3xl bg-zinc-900 border border-zinc-800"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors"
          >
            <svg className="w-5 h-5 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Header */}
          <div className="relative h-48 bg-gradient-to-br from-zinc-800 to-zinc-900 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 to-transparent z-10" />
            <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-gradient-to-r from-emerald-500/20 to-blue-500/20 blur-3xl" />

            {/* Badges */}
            <div className="absolute bottom-4 left-6 z-20 flex items-center gap-3">
              <span className={`px-3 py-1.5 rounded-full text-xs font-medium backdrop-blur-sm ${
                study.service === 'AI Implementation'
                  ? 'bg-blue-500/20 border border-blue-500/30 text-blue-400'
                  : study.service === 'VibeCode'
                    ? 'bg-violet-500/20 border border-violet-500/30 text-violet-400'
                    : 'bg-emerald-500/20 border border-emerald-500/30 text-emerald-400'
              }`}>
                {study.service}
              </span>
              <span className="px-3 py-1.5 rounded-full bg-zinc-800/50 border border-zinc-700/50 text-zinc-400 text-xs">
                {study.industry}
              </span>
            </div>

            {/* ROI Badge */}
            <div className="absolute bottom-4 right-6 z-20">
              <div className="px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-500/30 backdrop-blur-sm">
                <span className="text-emerald-400 text-lg font-bold">{study.roi} ROI</span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 leading-tight">
              {study.headline}
            </h2>

            {/* Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {study.metrics.map((metric, i) => (
                <div key={i} className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700/30">
                  <p className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-400">
                    {metric.value}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">{metric.label}</p>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="space-y-6 mb-8">
              <div>
                <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-2">The Situation</h3>
                <p className="text-zinc-300 leading-relaxed">{study.situation}</p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-2">The Solution</h3>
                <p className="text-zinc-300 leading-relaxed">{study.solution}</p>
              </div>
            </div>

            {/* Quote */}
            <div className="p-6 rounded-xl bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border border-emerald-500/20 mb-8">
              <p className="text-lg text-white italic mb-4">"{study.quote.text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500/20 to-blue-500/20 border border-zinc-700 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">
                    {study.quote.author.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div>
                  <p className="text-white font-medium">{study.quote.author}</p>
                  <p className="text-zinc-500 text-sm">{study.quote.company}</p>
                </div>
              </div>
            </div>

            {/* Technologies */}
            <div className="mb-8">
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-3">Technologies Used</h3>
              <div className="flex flex-wrap gap-2">
                {study.technologies.map((tech) => (
                  <span key={tech} className="px-3 py-1.5 rounded-lg bg-zinc-800/50 text-zinc-300 text-sm">
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-white text-zinc-900 font-semibold rounded-xl hover:shadow-lg hover:shadow-white/10 transition-all"
              >
                Get Similar Results
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <button
                onClick={onClose}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-zinc-800/50 border border-zinc-700/50 text-zinc-300 font-semibold rounded-xl hover:bg-zinc-800 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ============================================
// Related Case Studies Carousel
// ============================================
interface RelatedCarouselProps {
  studies: CaseStudy[];
  currentId: string;
  onSelect: (study: CaseStudy) => void;
}

const RelatedCarousel: React.FC<RelatedCarouselProps> = ({ studies, currentId, onSelect }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const related = studies.filter(s => s.id !== currentId).slice(0, 4);

  const scroll = (direction: 'left' | 'right') => {
    if (!containerRef.current) return;
    const scrollAmount = 320;
    containerRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  if (related.length === 0) return null;

  return (
    <section className="py-12 sm:py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-bold text-white">Related Case Studies</h2>
          <div className="flex gap-2">
            <button
              onClick={() => scroll('left')}
              className="w-10 h-10 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors"
            >
              <svg className="w-5 h-5 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => scroll('right')}
              className="w-10 h-10 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors"
            >
              <svg className="w-5 h-5 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        <div
          ref={containerRef}
          className="flex gap-6 overflow-x-auto scrollbar-hide pb-4"
          style={{ scrollSnapType: 'x mandatory' }}
        >
          {related.map((study) => (
            <motion.div
              key={study.id}
              className="flex-shrink-0 w-80 cursor-pointer"
              style={{ scrollSnapAlign: 'start' }}
              whileHover={{ y: -5 }}
              onClick={() => onSelect(study)}
            >
              <div className="h-full rounded-2xl bg-zinc-900/80 border border-zinc-800/50 hover:border-zinc-700/50 overflow-hidden transition-colors">
                <div className="h-32 bg-gradient-to-br from-zinc-800 to-zinc-900 relative">
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-xs font-bold">
                      {study.roi} ROI
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <span className="text-xs text-zinc-500">{study.industry}</span>
                  <h3 className="text-white font-semibold mt-1 line-clamp-2">{study.shortTitle}</h3>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// Main Page Component
// ============================================
export const CaseStudiesPage: React.FC = () => {
  const [selectedIndustry, setSelectedIndustry] = useState('All');
  const [selectedService, setSelectedService] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [previewStudy, setPreviewStudy] = useState<CaseStudy | null>(null);

  // Filter case studies
  const filteredStudies = useMemo(() => {
    return caseStudies.filter(study => {
      const matchesIndustry = selectedIndustry === 'All' || study.industry === selectedIndustry;
      const matchesService = selectedService === 'All' || study.service === selectedService;
      const matchesSearch = searchQuery === '' ||
        study.headline.toLowerCase().includes(searchQuery.toLowerCase()) ||
        study.shortTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
        study.industry.toLowerCase().includes(searchQuery.toLowerCase()) ||
        study.technologies.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));

      return matchesIndustry && matchesService && matchesSearch;
    });
  }, [selectedIndustry, selectedService, searchQuery]);

  // Close modal on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPreviewStudy(null);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Lock body scroll when modal open
  useEffect(() => {
    if (previewStudy) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [previewStudy]);

  return (
    <main className="bg-[#0a0a0a]">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        {/* Animated background blobs */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-[150px]"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
            x: [0, 50, 0],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[120px]"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 md:py-32">
          <div className="max-w-4xl">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-zinc-800/50 border border-zinc-700/50 backdrop-blur-sm"
            >
              <motion.span
                className="w-1.5 h-1.5 rounded-full bg-emerald-400"
                animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <span className="text-zinc-300 text-sm font-medium">Case Studies</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8 leading-[1.1]"
            >
              Don't Take Our Word For It.
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-400">
                Look At The Numbers.
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl text-zinc-400 leading-relaxed max-w-3xl mb-12"
            >
              Here's what happens when companies stop talking about AI and start deploying it.
            </motion.p>

            {/* Stats row */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="flex flex-wrap gap-6"
            >
              {[
                { value: '$1.5M+', label: 'Total Client Savings', color: 'emerald' },
                { value: '14 days', label: 'Avg. Time to Deploy', color: 'blue' },
                { value: '100%', label: 'Project Success Rate', color: 'violet' },
              ].map((stat, index) => (
                <div key={index} className="flex items-center gap-4 px-5 py-4 rounded-2xl bg-zinc-900/50 border border-zinc-800/50">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    stat.color === 'emerald' ? 'bg-emerald-500/10' :
                    stat.color === 'blue' ? 'bg-blue-500/10' : 'bg-violet-500/10'
                  }`}>
                    {stat.color === 'emerald' && (
                      <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                    {stat.color === 'blue' && (
                      <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                    {stat.color === 'violet' && (
                      <svg className="w-6 h-6 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <p className={`text-2xl font-bold ${
                      stat.color === 'emerald' ? 'text-emerald-400' :
                      stat.color === 'blue' ? 'text-blue-400' : 'text-violet-400'
                    }`}>
                      {stat.value}
                    </p>
                    <p className="text-zinc-500 text-sm">{stat.label}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Bottom gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
      </section>

      {/* Filters Section */}
      <section className="relative py-6 sm:py-8 bg-zinc-900/30 border-y border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6">
            {/* Filter rows */}
            <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <div className="flex items-center gap-3">
                  <span className="text-zinc-500 text-sm font-medium">Industry:</span>
                  <FilterPills
                    options={industries}
                    selected={selectedIndustry}
                    onChange={setSelectedIndustry}
                  />
                </div>
              </div>
              <div className="w-full sm:w-72">
                <SearchInput value={searchQuery} onChange={setSearchQuery} />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-zinc-500 text-sm font-medium">Service:</span>
                <FilterPills
                  options={services}
                  selected={selectedService}
                  onChange={setSelectedService}
                />
              </div>

              {/* Results count */}
              <motion.p
                key={filteredStudies.length}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-zinc-500 text-sm"
              >
                {filteredStudies.length} {filteredStudies.length === 1 ? 'result' : 'results'}
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="relative py-12 sm:py-16 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatePresence mode="popLayout">
            {filteredStudies.length > 0 ? (
              <motion.div
                layout
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr"
              >
                {filteredStudies.map((study, index) => (
                  <CaseStudyCard
                    key={study.id}
                    study={study}
                    index={index}
                    onPreview={setPreviewStudy}
                  />
                ))}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-20"
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-zinc-800 flex items-center justify-center">
                  <svg className="w-8 h-8 text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">No case studies found</h3>
                <p className="text-zinc-500 mb-6">Try adjusting your filters or search query</p>
                <button
                  onClick={() => {
                    setSelectedIndustry('All');
                    setSelectedService('All');
                    setSearchQuery('');
                  }}
                  className="px-6 py-2 rounded-lg bg-zinc-800 text-white hover:bg-zinc-700 transition-colors"
                >
                  Clear all filters
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Related Case Studies Carousel */}
      {previewStudy && (
        <RelatedCarousel
          studies={caseStudies}
          currentId={previewStudy.id}
          onSelect={setPreviewStudy}
        />
      )}

      {/* Bottom CTA Section */}
      <section className="relative py-16 sm:py-24 md:py-32 overflow-hidden">
        {/* Animated background */}
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-gradient-to-r from-emerald-600/10 via-blue-600/10 to-emerald-600/10 rounded-full blur-[150px]"
          animate={{
            scale: [1, 1.15, 1],
            opacity: [0.4, 0.6, 0.4],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Top border */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-zinc-800/50 border border-zinc-700/50 backdrop-blur-sm"
          >
            <motion.span
              className="w-1.5 h-1.5 rounded-full bg-emerald-400"
              animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span className="text-zinc-300 text-sm font-medium">Your Turn</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6"
          >
            These Companies Were Skeptical Too.
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-400">
              Then They Saw The Results.
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-xl text-zinc-400 mb-12 max-w-2xl mx-auto"
          >
            Your competitors are already deploying AI. The question isn't whether you'll adopt it — it's whether you'll be leading or catching up.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <motion.a
              href="/contact"
              className="group relative inline-flex items-center gap-3 px-10 py-5 bg-white text-zinc-900 font-semibold text-lg rounded-xl transition-all duration-300 hover:shadow-2xl hover:shadow-white/20"
              whileHover={{ y: -3, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Book Your Free Strategy Session
              <motion.svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </motion.svg>
            </motion.a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="mt-8 text-zinc-500 text-sm"
          >
            No commitment. No sales pitch. Just a clear map of where AI can cut costs and drive revenue in your business.
          </motion.p>

          {/* Trust indicators */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="mt-12 flex flex-wrap justify-center gap-8"
          >
            {[
              { icon: '🔒', text: 'NDA Protected' },
              { icon: '⏱️', text: '30-Min Call' },
              { icon: '📊', text: 'Custom Roadmap' },
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-2 text-zinc-500">
                <span>{item.icon}</span>
                <span className="text-sm">{item.text}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Preview Modal */}
      {previewStudy && (
        <PreviewModal study={previewStudy} onClose={() => setPreviewStudy(null)} />
      )}
    </main>
  );
};

export default CaseStudiesPage;
